/**
* 登录基础类
*/
import '@common/polyfill/src/Object.assign'
import domain from '@common/domain'
import url from '@common/url'
import cookie from 'js-cookie'
import ajax from '@finance/ajax'

import { isLoginCookie, callbackHandler, mixins, getUserData } from '../utils'
import { loginTypes, accountTypes } from '../dict'
import { openAccount } from '../account/account'

// 类型匹配
const matchType = (refer, values) => {
  const _refer = (typeof refer === 'string') ? refer.toLowerCase() : refer
  const _values = Array.isArray(values) ? values : [values]
  return _values.indexOf(_refer) !== -1
}

const COOKIE_EXPIRE_TIME = 30 // 单位天

@mixins({ openAccount, loginTypes, accountTypes })
class BaseAuth {
  // 是否命中客户端
  isClientHit = false
  // 是否依赖isLogin cookie
  isRelyOnIsLoginCookie = true
  // 配置
  options = {
    type: loginTypes.normal, // 登录类型
    accountType: accountTypes.none, // 开户类型
    forwardUrl: window.location.href, // web登录或开户后的跳转url
    origin: '', // 开户的渠道标识
    currentView: false, // 是否在当前窗口打开开户url
    isNeedUserData: false, // 是否强制需要用户数据
    isShowAccountCustodyDialog: true, // 存管账户是否先弹窗提示
    webLoginType: 'ssj', // web登录时调用的页面，【ssj:随手记，sgj:随管家】
    success: () => { }, // 成功回调
    error: () => { }, // 失败回调
    cancel: () => { } // 取消登录回调
  }
  // 登录类型
  loginType = {}
  // 需要开户类型
  accountType = {}
  constructor(options) {
    this.options = Object.assign(this.options, options)
    this.loginType = {
      normal: matchType(this.options.type, [0, this.loginTypes.normal]),
      force: matchType(this.options.type, [1, this.loginTypes.force]),
      silent: matchType(this.options.type, [2, this.loginTypes.silent]),
      tokenExpire: matchType(this.options.type, [3, this.loginTypes.tokenExpire]),
      tokenError: matchType(this.options.type, [4, this.loginTypes.tokenError]),
    }
    this.accountType = {
      none: matchType(this.options.accountType, [1, this.accountTypes.none]),
      custody: matchType(this.options.accountType, [2, this.accountTypes.custody]),
      acc: matchType(this.options.accountType, [3, this.accountTypes.acc]),
      cardbull: matchType(this.options.accountType, [4, this.accountTypes.cardbull]),
    }
  }
  // 重设登录类型
  // 用于登录失败时调用
  resetLoginType(key) {
    Object.keys(this.loginType).forEach((item) => {
      this.loginType[item] = false
    })
    try {
      this.loginType[key] = true
    } catch (e) {
      console.error(e)
    }
  }
  // 请求登录接口
  requestLoginApi(params, callbacks = {
    tokenExpire() {},
    tokenError() {},
    accountError() {},
  }) {
    let apiUrl = null
    let apiParams = null
    if (params.token) {
      apiUrl = `${domain.api}/userws/ws/login/v1/loginByToken`
      apiParams = {
        userName: params.userName,
        token: params.token,
      }
    } else if (params.userName && params.password) {
      apiUrl = `${domain.api}/userws/ws/login/v1/loginByPwd`
      apiParams = {
        userName: params.userName,
        password: params.password,
      }
    } else {
      apiUrl = `${domain.api}/userws/ws/login/v1/logout`
      apiParams = {
        userName: null,
        password: null,
      }
    }
    ajax.request({ url: apiUrl, body: apiParams }).then((result) => {
      if (result.succeed) {
        // result.data.token 是 loginByPwd 接口返回的
        BaseAuth.setOAuth2Token(params.token || (result.data ? result.data.token : ''))
        // 登录成功，判断是否需要用户信息和开户信息
        this.requestLoginApiSuccessCallback()
      } else if (result.code === '999901') {
        // token过期
        callbackHandler(callbacks.tokenExpire)
      } else if (result.code === '999902') {
        // token错误
        callbackHandler(callbacks.tokenError)
      } else if (result.code === '200000-005-004') {
        // 账号验证失败
        callbackHandler(callbacks.accountError)
      } else {
        console.error('登录失败', 'ApiLogin Error:', result.msg)
        callbackHandler(this.options.error, { msg: result.msg })
      }
    }).catch((error) => {
      console.log('RequestLoginApi Error:', error)
      callbackHandler(this.options.error, { msg: '登录失败，请重试' })
    })
  }
  // 登录成功或者退出登录成功
  async requestLoginApiSuccessCallback() {
    // 登录成功，判断是否需要用户信息和开户信息
    // 必须要在登录情况下才能去回去账户信息
    if (isLoginCookie() && (this.accountType.acc || this.accountType.custody)) {
      this.openAccount(this.options)
    } else {
      try {
        const userData = await getUserData(this.options.isNeedUserData)
        callbackHandler(this.options.success, userData)
      } catch (error) {
        console.log('GetUserData Error:', error)
        callbackHandler(this.options.error, { msg: '获取用户信息失败，请重试' })
      }
    }
  }
  // 检验cookie
  checkIsLogin() {
    if (isLoginCookie() && this.isRelyOnIsLoginCookie) {
      return true
    }
    return false
  }
  static setOAuth2Token(token) {
    if (token) {
      cookie.set('ssj_token', token, { domain: url('domain'), expires: COOKIE_EXPIRE_TIME })
    }
  }
}

export default BaseAuth
